import { useState } from "react";

interface TestimonialsSectionProps {
  onGetQuote: () => void;
}

const testimonials = [
  {
    quote: "Their work continues to provide considerable value as their performance and execution, adapts quickly and is responsive to an evolving scope of work, all the while maintaining project timelines and ensuring a quality build-out.",
    name: "Marie Tatarka",
    title: "Asset Manager, Urban Properties",
    company: "KingSett Capital",
  },
  {
    quote: "Over the 15 years I have worked with Steve Page of Taurus Contracting I have always been more than satisfied with the high quality of work and their attention to detail and customer satisfaction. I will definitely endorse Taurus Contracting for any construction related project in the future.",
    name: "Steve Barratt",
    title: "Director of Leasing",
    company: "TD Canada Trust",
  },
];

export default function TestimonialsSection({ onGetQuote }: TestimonialsSectionProps) {
  const [active, setActive] = useState(0);

  return (
    <section id="testimonials" className="w-full py-28 px-6 lg:px-12 bg-stone-900">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: heading + CTA */}
          <div>
            <span
              className="text-stone-500 text-xs uppercase tracking-[0.28em] font-medium block mb-5"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Client Feedback
            </span>
            <h2
              className="text-white font-semibold leading-tight mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.2rem, 4vw, 3.4rem)" }}
            >
              What Clients
              <br />
              <span className="font-light italic text-stone-300">Are Saying</span>
            </h2>
            <p
              className="text-stone-400 text-sm leading-relaxed mb-10 max-w-sm"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Real feedback from real clients. Taurus Contracting has built lasting relationships with residential and commercial clients across Ontario.
            </p>
            <button
              onClick={onGetQuote}
              className="flex items-center gap-3 px-8 py-4 bg-white text-stone-900 font-semibold text-sm uppercase tracking-[0.12em] rounded-sm hover:bg-stone-100 transition-all whitespace-nowrap cursor-pointer"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Get Your Free Quote
              <i className="ri-arrow-right-line text-sm"></i>
            </button>

            {/* Dots */}
            <div className="flex gap-3 mt-10">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActive(idx)}
                  className={`transition-all duration-300 cursor-pointer rounded-full ${
                    idx === active ? "w-8 h-2 bg-white" : "w-2 h-2 bg-stone-600 hover:bg-stone-400"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right: testimonial card */}
          <div className="relative">
            <div className="bg-stone-800 rounded-sm p-10 lg:p-12 border border-stone-700">
              {/* Quote mark */}
              <div className="mb-6">
                <i className="ri-double-quotes-l text-stone-600 text-4xl"></i>
              </div>

              <p
                className="text-stone-200 text-lg leading-relaxed mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.1rem, 1.8vw, 1.3rem)" }}
              >
                {testimonials[active].quote}
              </p>

              <div className="flex items-center gap-4 pt-6 border-t border-stone-700">
                <div className="w-10 h-10 rounded-full bg-stone-700 flex items-center justify-center flex-shrink-0">
                  <span className="text-stone-300 text-sm font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {testimonials[active].name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {testimonials[active].name}
                  </p>
                  <p className="text-stone-400 text-xs mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {testimonials[active].title} · {testimonials[active].company}
                  </p>
                </div>
              </div>
            </div>

            {/* Nav arrows */}
            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={() => setActive((active - 1 + testimonials.length) % testimonials.length)}
                className="w-10 h-10 flex items-center justify-center border border-stone-700 text-stone-400 hover:border-white hover:text-white transition-all rounded-sm cursor-pointer"
              >
                <i className="ri-arrow-left-line text-sm"></i>
              </button>
              <button
                onClick={() => setActive((active + 1) % testimonials.length)}
                className="w-10 h-10 flex items-center justify-center border border-stone-700 text-stone-400 hover:border-white hover:text-white transition-all rounded-sm cursor-pointer"
              >
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
